import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { confirmDelete } from "@/lib/confirm";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { AppIcon } from "@/components/apps/AppIcon";
import { BrandPickerDialog, BrandSubRow } from "@/components/apps/BrandSubscriptions";
import { Loader2, ArrowLeft, LayoutGrid, ExternalLink, Check, ArrowUpCircle, FileText, Download, CreditCard, Lock, Trash2, RotateCcw } from "lucide-react";

const money = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const RichText = ({ html, className = "" }) => (html ? <div className={`prose prose-sm max-w-none text-[#595D62] ${className}`} dangerouslySetInnerHTML={{ __html: html }} /> : null);
const fmtDate = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } };

const Field = ({ label, testId, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="font-mono-data text-[10px] uppercase tracking-wider text-[#9FA1A4] mb-1 block">{label}</label>
    <Input data-testid={testId} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="bg-[#F6F6FD] border-[#EEEFEF] h-11" />
  </div>
);

function CheckoutForm({ plan, brand, testMode, onDone }) {
  const stripe = useStripe();
  const elements = useElements();
  const [billing, setBilling] = useState({ name: "", email: "", line1: "", city: "", state: "", postal_code: "", country: "AU" });
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (v) => setBilling((b) => ({ ...b, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!billing.name.trim() || !billing.email.trim()) { setErr("Name and email are required."); return; }
    setPaying(true); setErr("");
    const fail = (m) => { setErr(m); toast.error(m); setPaying(false); };
    const { error: subErr } = await elements.submit();
    if (subErr) return fail(subErr.message || "Please check your payment details.");
    let data;
    try { ({ data } = await api.post("/apps/subscribe", { plan_id: plan.id, billing, brand_id: brand?.id || null })); }
    catch (e2) { return fail(formatApiError(e2.response?.data?.detail)); }
    const { error } = await stripe.confirmPayment({
      elements, clientSecret: data.client_secret, redirect: "if_required",
      confirmParams: { payment_method_data: { billing_details: {
        name: billing.name, email: billing.email,
        address: { line1: billing.line1 || "", line2: "", city: billing.city || "", state: billing.state || "", postal_code: billing.postal_code || "", country: billing.country || "AU" },
      } } },
    });
    if (error) return fail(error.message || "Payment could not be completed.");
    try { await api.post(`/apps/subscriptions/${data.subscription_record_id}/sync`); } catch (e3) { /* webhook will catch up */ }
    toast.success("Subscription active");
    onDone();
  };

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="checkout-form">
      <section className="space-y-3">
        <h2 className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#723FFF]">Billing details</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Billing name" testId="billing-name" value={billing.name} onChange={set("name")} />
          <Field label="Email" testId="billing-email" type="email" value={billing.email} onChange={set("email")} />
        </div>
        <Field label="Address" testId="billing-line1" value={billing.line1} onChange={set("line1")} placeholder="Street address" />
        <div className="grid grid-cols-3 gap-3">
          <Field label="City" testId="billing-city" value={billing.city} onChange={set("city")} />
          <Field label="State" testId="billing-state" value={billing.state} onChange={set("state")} />
          <Field label="Postcode" testId="billing-postal" value={billing.postal_code} onChange={set("postal_code")} />
        </div>
      </section>
      <section className="space-y-3">
        <h2 className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#723FFF]">Payment</h2>
        <PaymentElement options={{ layout: "tabs", fields: { billingDetails: { name: "never", email: "never", address: "never" } } }} />
      </section>
      {err && <p className="text-sm text-rose-600" data-testid="payment-error">{err}</p>}
      <Button type="submit" data-testid="payment-submit" disabled={!stripe || paying} className="w-full h-12 bg-[#06060C] text-white hover:bg-[#1c1c24] rounded-full text-base">
        {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Subscribe · {money(plan.price)}/mo</>}
      </Button>
      <p className="text-[11px] text-[#9FA1A4] text-center flex items-center justify-center gap-1.5"><Lock className="h-3 w-3" /> Secure payment by Stripe · cancel anytime</p>
      {testMode && <p className="text-[11px] text-[#9FA1A4] text-center" data-testid="test-card-hint">Test card 4242 4242 4242 4242 · any future expiry · any CVC</p>}
    </form>
  );
}

function PlanSummary({ app, plan, brand }) {
  return (
    <div className="space-y-5" data-testid="checkout-summary">
      <AppIcon appId={app.id} icon={app.icon} size="lg" testId="checkout-app-icon" />
      <div>
        <p className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#723FFF] mb-2">{app.name}</p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C]" data-testid="checkout-plan-name">{plan.name}</h1>
        {brand && <p className="mt-2 text-sm text-[#595D62]" data-testid="checkout-brand">For <span className="font-semibold text-[#06060C]">{brand.name}</span></p>}
      </div>
      <p className="font-display text-4xl font-semibold text-[#06060C]" data-testid="checkout-price">{money(plan.price)}<span className="text-base font-normal text-[#9FA1A4]">/month</span></p>
      <p className="text-xs text-[#9FA1A4] -mt-3">incl. GST · billed monthly</p>
      {plan.description && <p className="text-[#595D62]">{plan.description}</p>}
      <RichText html={plan.rich_text} />
      <div className="rounded-2xl border border-[#EEEFEF] bg-white p-4 flex items-center justify-between text-sm">
        <span className="text-[#595D62]">Due today</span>
        <span className="font-semibold text-[#06060C]" data-testid="checkout-due-today">{money(plan.price)}</span>
      </div>
    </div>
  );
}

function CheckoutScreen({ app, plan, brand, publishableKey, onClose, onSubscribed }) {
  const [stripePromise] = useState(() => (publishableKey ? loadStripe(publishableKey) : null));
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  const options = { mode: "subscription", amount: Math.round(Number(plan.price || 0) * 100), currency: "aud",
                    appearance: { theme: "stripe", variables: { colorPrimary: "#723FFF", borderRadius: "10px" } } };
  return (
    <div className="fixed inset-0 z-50 bg-white overflow-y-auto animate-in fade-in duration-200" data-testid="checkout-screen">
      <div className="grid lg:grid-cols-2 min-h-full">
        <div className="bg-[#F7F4FF] px-6 md:px-12 lg:px-16 py-8 lg:py-12">
          <button onClick={onClose} data-testid="checkout-back-btn" className="text-sm text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1.5 mb-10"><ArrowLeft className="h-4 w-4" /> Back</button>
          <div className="max-w-md lg:ml-auto lg:mr-8"><PlanSummary app={app} plan={plan} brand={brand} /></div>
        </div>
        <div className="px-6 md:px-12 lg:px-16 py-8 lg:py-12 lg:pt-[6.5rem]">
          <div className="max-w-md lg:ml-8">
            {stripePromise ? (
              <Elements stripe={stripePromise} options={options}>
                <CheckoutForm plan={plan} brand={brand} testMode={publishableKey.startsWith("pk_test")} onDone={onSubscribed} />
              </Elements>
            ) : <p className="text-sm text-[#9FA1A4]">Payments aren't set up yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function InvoicesPanel() {
  const [invoices, setInvoices] = useState(null);
  const [canManage, setCanManage] = useState(false);
  useEffect(() => { api.get("/apps/invoices").then(({ data }) => { setInvoices(data.invoices || []); setCanManage(!!data.can_manage); }).catch(() => setInvoices([])); }, []);
  const setHidden = async (inv, hidden) => {
    if (hidden && !(await confirmDelete({ title: "Remove invoice?", message: `Hide invoice ${inv.number || inv.id} from this client's Apps page? It stays in Stripe and you can restore it.`, confirmText: "Remove" }))) return;
    try {
      await api.post(`/apps/invoices/${inv.id}/visibility`, { hidden });
      setInvoices((list) => list.map((x) => (x.id === inv.id ? { ...x, hidden } : x)));
      toast.success(hidden ? "Invoice removed from the client's view" : "Invoice restored");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (invoices === null) return <div className="py-6 text-center"><Loader2 className="h-4 w-4 animate-spin text-[#c4c5cc] mx-auto" /></div>;
  if (invoices.length === 0) return <p className="text-sm text-[#9FA1A4]">No invoices yet.</p>;
  return (
    <div className="space-y-2" data-testid="invoices-list">
      {invoices.map((inv) => (
        <div key={inv.id} data-testid={`invoice-${inv.id}`} className={`flex items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-opacity ${inv.hidden ? "border-dashed border-[#e2e2ea] bg-[#FAFAFD] opacity-60" : "border-[#f0f0f3] bg-white"}`}>
          {inv.app ? <AppIcon appId={inv.app.id} icon={inv.app.icon} size="sm" testId={`invoice-app-icon-${inv.id}`} /> : <span className="h-7 w-7 flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-[#9FA1A4]" /></span>}
          <div className="min-w-0 flex-1">
            <p className="text-sm text-[#06060C] truncate">{inv.number || inv.id} · {money(inv.amount)} {inv.currency}{inv.app && <span className="text-[#9FA1A4]"> · {inv.app.name}{inv.app.brand ? ` · ${inv.app.brand}` : ""}</span>}</p>
            <p className="text-[11px] text-[#9FA1A4]">{fmtDate(inv.created)} · <span className="capitalize">{inv.status}</span>{inv.hidden && <span data-testid={`invoice-hidden-${inv.id}`} className="ml-1.5 text-amber-700">· Hidden from client</span>}</p>
          </div>
          {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-[#723FFF] hover:text-[#5b2fd6] shrink-0" title="View invoice"><ExternalLink className="h-4 w-4" /></a>}
          {inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-[#723FFF] hover:text-[#5b2fd6] shrink-0" title="Download PDF"><Download className="h-4 w-4" /></a>}
          {canManage && (inv.hidden
            ? <button data-testid={`invoice-restore-${inv.id}`} onClick={() => setHidden(inv, false)} title="Restore for client" className="text-[#595D62] hover:text-[#723FFF] shrink-0"><RotateCcw className="h-4 w-4" /></button>
            : <button data-testid={`invoice-delete-${inv.id}`} onClick={() => setHidden(inv, true)} title="Remove from client's view" className="text-[#c4c5cc] hover:text-rose-600 shrink-0"><Trash2 className="h-4 w-4" /></button>)}
        </div>
      ))}
    </div>
  );
}

function AppDetail({ appId, onBack }) {
  const [data, setData] = useState(null);
  const [subscribePlan, setSubscribePlan] = useState(null);
  const [pickPlan, setPickPlan] = useState(null);
  const [brand, setBrand] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get(`/apps/${appId}`); setData(data); };
  const closeCheckout = useCallback(() => setSubscribePlan(null), []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [appId]);

  const cancel = async (sub) => {
    if (!(await confirmDelete({ message: "Cancel this subscription at the end of the current billing period?", confirmText: "Cancel subscription" }))) return;
    setBusy(true);
    try { await api.post(`/apps/subscriptions/${sub.id}/cancel`); toast.success("Will cancel at period end"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const resume = async (sub) => {
    setBusy(true);
    try { await api.post(`/apps/subscriptions/${sub.id}/resume`); toast.success("Subscription resumed"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const upgrade = async (sub, targetPlan) => {
    setBusy(true);
    try { await api.post(`/apps/subscriptions/${sub.id}/upgrade`, { plan_id: targetPlan.id }); toast.success(`Upgrading to ${targetPlan.name} at next billing date`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!data) return <div className="py-24 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>;

  return (
    <div data-testid="app-detail">
      <button onClick={onBack} className="text-sm text-[#723FFF] hover:text-[#5b2fd6] flex items-center gap-1.5 mb-5"><ArrowLeft className="h-4 w-4" /> Back to apps</button>
      <div className="mb-8">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-[#06060C] flex items-center gap-3"><AppIcon appId={data.app.id} icon={data.app.icon} size="lg" testId="app-detail-icon" />{data.app.name}</h1>
        {data.app.description && <p className="text-[#595D62] mt-1.5">{data.app.description}</p>}
        {data.app.website && <a href={data.app.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-[#723FFF] hover:text-[#5b2fd6] mt-2">{data.app.website} <ExternalLink className="h-3.5 w-3.5" /></a>}
        <RichText html={data.app.rich_text} className="mt-3" />
      </div>

      <h2 className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#723FFF] mb-3">Subscriptions</h2>
      {data.plans.length === 0 ? <p className="text-sm text-[#9FA1A4]">No subscriptions available for your workspace yet.</p> : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.plans.map((p) => {
            const subs = p.subscriptions || [];
            return (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className={`rounded-2xl border p-5 flex flex-col ${subs.length ? "border-[#723FFF]/40 bg-[#F7F4FF]" : "border-[#EEEFEF] bg-white"}`}>
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display text-lg font-semibold text-[#06060C] flex items-center gap-2"><AppIcon appId={data.app.id} icon={data.app.icon} size="sm" testId={`plan-app-icon-${p.id}`} />{p.name}</h3>
                  {subs.length > 0 && <span data-testid={`plan-active-${p.id}`} className="font-mono-data text-[9px] uppercase tracking-wider text-[#1f9d55] bg-[#1f9d55]/10 rounded-full px-2 py-0.5 flex items-center gap-1"><Check className="h-3 w-3" /> {subs.length} active</span>}
                </div>
                <p className="font-display text-2xl font-semibold text-[#06060C] mt-2">{money(p.price)}<span className="text-sm font-normal text-[#9FA1A4]">/mo per brand</span></p>
                <p className="text-[11px] text-[#9FA1A4]">incl. GST</p>
                {p.description && <p className="text-sm text-[#595D62] mt-3">{p.description}</p>}
                <RichText html={p.rich_text} className="mt-2" />
                <div className="mt-auto pt-4 space-y-2">
                  {subs.map((sub) => <BrandSubRow key={sub.id} sub={sub} upgrades={p.upgrade_targets || []} busy={busy} onCancel={cancel} onResume={resume} onUpgrade={upgrade} />)}
                  <Button data-testid={`subscribe-btn-${p.id}`} onClick={() => setPickPlan(p)} variant={subs.length ? "outline" : "default"} className={`w-full rounded-full ${subs.length ? "border-[#723FFF]/40 text-[#723FFF] bg-white hover:bg-[#F7F4FF]" : "bg-[#06060C] text-white hover:bg-[#1c1c24]"}`}>
                    <CreditCard className="h-4 w-4 mr-1.5" /> {subs.length ? "Add another brand" : "Subscribe now"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h2 className="font-mono-data text-[10px] uppercase tracking-[0.2em] text-[#723FFF] mt-10 mb-3">Invoices</h2>
      <InvoicesPanel />

      {pickPlan && <BrandPickerDialog appId={data.app.id} plan={pickPlan} brands={data.brands} takenIds={data.taken_brand_ids} workspaceName={data.workspace_name}
        onClose={() => setPickPlan(null)} onBrandAdded={(b) => setData((d) => ({ ...d, brands: [...d.brands, b] }))} onBrandRemoved={(id) => setData((d) => ({ ...d, brands: d.brands.filter((x) => x.id !== id) }))}
        onPick={(b) => { setBrand(b.id ? b : { id: "", name: data.workspace_name || "Main account" }); setSubscribePlan(pickPlan); setPickPlan(null); }} />}
      {subscribePlan && <CheckoutScreen app={data.app} plan={subscribePlan} brand={brand} publishableKey={data.publishable_key} onClose={closeCheckout} onSubscribed={() => { setSubscribePlan(null); load(); }} />}
    </div>
  );
}

export default function Apps() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [openApp, setOpenApp] = useState(null);

  useEffect(() => { api.get("/apps").then(({ data }) => setData(data)).catch(() => setData({ apps: [] })); }, []);

  return (
    <main className="px-6 md:px-12 py-10 max-w-5xl mx-auto" data-testid="apps-page">
      {openApp ? (
        <AppDetail appId={openApp} onBack={() => setOpenApp(null)} />
      ) : !data ? (
        <div className="py-24 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#c4c5cc] mx-auto" /></div>
      ) : (
        <>
          <p className="font-mono-data text-[10px] uppercase tracking-[0.22em] text-[#723FFF] mb-2.5">Marketplace</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#06060C] mb-8">Apps</h1>
          {data.apps.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-[#e2e2ea] bg-[#FCFCFE] py-20 text-center" data-testid="apps-empty">
              <LayoutGrid className="h-7 w-7 text-[#c4c5cc] mx-auto mb-3" />
              <p className="text-sm text-[#9FA1A4]">No apps are available for your workspace yet.</p>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.apps.map((a) => (
                <div key={a.id} data-testid={`app-card-${a.id}`} className="rounded-2xl border border-[#EEEFEF] bg-white p-5 flex flex-col hover:border-[#d9ccff] transition-colors">
                  <AppIcon appId={a.id} icon={a.icon} className="mb-3" testId={`app-card-icon-${a.id}`} />
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg font-semibold text-[#06060C]">{a.name}</h3>
                    {a.subscribed && <span className="font-mono-data text-[9px] uppercase tracking-wider text-[#1f9d55] bg-[#1f9d55]/10 rounded-full px-2 py-0.5">Subscribed</span>}
                  </div>
                  {a.description && <p className="text-sm text-[#595D62] mt-1.5 line-clamp-3">{a.description}</p>}
                  {a.from_price > 0 && <p className="font-mono-data text-xs text-[#06060C] mt-2" data-testid={`app-from-price-${a.id}`}>from {money(a.from_price)}<span className="text-[#9FA1A4]">/mo</span></p>}
                  {a.website && <a href={a.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 text-xs text-[#723FFF] hover:text-[#5b2fd6] mt-2">Website <ExternalLink className="h-3 w-3" /></a>}
                  <div className="mt-auto pt-4">
                    <Button data-testid={`see-subs-${a.id}`} onClick={() => setOpenApp(a.id)} variant="outline" className="w-full border-[#EEEFEF] text-[#06060C] hover:bg-[#F6F6FD]">See subscriptions ({a.plan_count})</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </main>
  );
}
